import {
  type StepBuildCeremony,
  mergeStepBuildProps,
  sectionPageIntroMotions,
} from "@/components/benefits-discovery-v2/step-sequential-build";
import { cn } from "@/lib/utils";
import { track } from "@cakewalk/frontend-api";
import { ArrowRight, Check, MapPin, Plus, UserMinus, X } from "@cakewalk/ui/icons";
import {
  V2Button,
  V2Card,
  V2CardContent,
  V2DateInput,
  V2EnrollmentFlowMain,
  V2FormActions,
  V2Input,
  V2Select,
  V2SelectContent,
  V2SelectItem,
  V2SelectTrigger,
  V2SelectValue,
  V2Switch,
  V2Typography,
} from "@cakewalk/ui/v2";
import { useState } from "react";
import {
  SEX_OPTIONS,
  WAIVE_REASONS,
  formatZip,
  makeDependent,
  memberDependentsComplete,
  todayInputValue,
  validDateOfBirth,
  validZip,
} from "./data";
import type { Dependent, Sex, TeamMember, WaiveReason } from "./types";

/**
 * Step 4 of the company-census flow: dependents — manual entry only.
 *
 * The demo's tabbed "Ask my team" (employee-invite) path was dropped; the
 * employer types each person's spouse / children birthdates inline here. No
 * prices are shown — this isn't enrollment. The Submit button persists the
 * whole census (the orchestrator owns the mutation) and then advances.
 */

const MAX_KIDS = 8;

// Varied avatar tints so the roster isn't a wall of one color.
const AVATAR_TINTS = [
  "bg-cw-v2-blue-100 text-cw-v2-blue-600",
  "bg-cw-v2-coral-100 text-cw-v2-reliant-red",
  "bg-cw-v2-green-100 text-cw-v2-green-700",
] as const;

function memberInitials(m: TeamMember, fallback: string): string {
  return `${m.firstName.trim().charAt(0)}${m.lastName.trim().charAt(0)}`.toUpperCase() || fallback;
}

function Avatar({ member, index }: { member: TeamMember; index: number }) {
  const tint = AVATAR_TINTS[index % AVATAR_TINTS.length];
  return (
    <span
      aria-hidden="true"
      // Initials are member-derived text, not an input value, so the global `maskAllInputs`
      // does not reach them. See the note on the name below.
      data-sensitive
      className={cn(
        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold",
        tint,
      )}
    >
      {memberInitials(member, String(index + 1))}
    </span>
  );
}

export function CompanyCensusDependentsStep({
  members,
  playBuild = false,
  ceremony = null,
  isSubmitting = false,
  submitError,
  onChange,
  onBack,
  onSubmit,
}: {
  members: TeamMember[];
  playBuild?: boolean;
  ceremony?: StepBuildCeremony | null;
  isSubmitting?: boolean;
  submitError?: string;
  onChange: (next: TeamMember[]) => void;
  onBack: () => void;
  onSubmit: () => void;
}) {
  const team = members.length;

  // Required-field errors surface only once a field has been left (blurred),
  // never while the employer is still typing. Keyed by `${dependentId}:${field}`.
  const [touched, setTouched] = useState<Set<string>>(() => new Set());
  const markTouched = (depId: string, field: string) => () =>
    setTouched((prev) => new Set(prev).add(`${depId}:${field}`));
  const isTouched = (depId: string, field: string) => touched.has(`${depId}:${field}`);

  const updateMember = (id: string, patch: Partial<TeamMember>) =>
    onChange(members.map((m) => (m.id === id ? { ...m, dependentMethod: "manual", ...patch } : m)));

  const hasSpouse = (m: TeamMember) => m.dependents.some((d) => d.relationship === "spouse");
  const kidsOf = (m: TeamMember) => m.dependents.filter((d) => d.relationship === "child");

  const toggleSpouse = (m: TeamMember) => {
    if (hasSpouse(m)) {
      updateMember(m.id, { dependents: m.dependents.filter((d) => d.relationship !== "spouse") });
    } else {
      // tracking-plan: Flow 11 (company_census) > census_dependent_added (P2)
      track("census_dependent_added", {
        flow_name: "company_census",
        step_name: "dependents",
        relationship: "spouse",
      });
      updateMember(m.id, { dependents: [makeDependent("spouse"), ...m.dependents] });
    }
  };

  const addKid = (m: TeamMember) => {
    if (kidsOf(m).length >= MAX_KIDS) return;
    // tracking-plan: Flow 11 (company_census) > census_dependent_added (P2)
    track("census_dependent_added", {
      flow_name: "company_census",
      step_name: "dependents",
      relationship: "child",
    });
    updateMember(m.id, { dependents: [...m.dependents, makeDependent("child")] });
  };
  const removeDependent = (m: TeamMember, depId: string) => {
    // tracking-plan: Flow 11 (company_census) > census_dependent_removed (P2)
    track("census_dependent_removed", { flow_name: "company_census", step_name: "dependents" });
    updateMember(m.id, { dependents: m.dependents.filter((d) => d.id !== depId) });
  };
  const patchDependent = (m: TeamMember, depId: string, patch: Partial<Dependent>) =>
    updateMember(m.id, {
      dependents: m.dependents.map((d) => (d.id === depId ? { ...d, ...patch } : d)),
    });

  // ZIP-sharing toggle: on by default. Turning it OFF reveals a per-dependent
  // ZIP field, pre-filled from the member's ZIP for any dependent without one.
  const sameZip = (m: TeamMember) => m.dependentsSameZip !== false;
  const toggleSameZip = (m: TeamMember) => {
    const next = !sameZip(m);
    updateMember(m.id, {
      dependentsSameZip: next,
      dependents: next
        ? m.dependents
        : m.dependents.map((d) => (d.zip.trim() ? d : { ...d, zip: m.zip })),
    });
  };

  // Enrolled unless explicitly waived (undefined === enrolled).
  const isEnrolling = (m: TeamMember) => m.enrolling !== false;
  const toggleEnrolling = (m: TeamMember) =>
    updateMember(m.id, {
      enrolling: !isEnrolling(m),
      dependents: isEnrolling(m) ? [] : m.dependents,
      // The waive reason only applies while waived — clear it on re-enroll.
      notEnrollingReason: isEnrolling(m) ? m.notEnrollingReason : undefined,
    });
  const setWaiveReason = (m: TeamMember, reason: WaiveReason) => {
    // tracking-plan: Flow 11 (company_census) > census_member_waived (P2)
    track("census_member_waived", {
      flow_name: "company_census",
      step_name: "dependents",
      waive_reason: reason,
    });
    updateMember(m.id, { notEnrollingReason: reason });
  };

  const enrolledCount = members.filter(isEnrolling).length;
  const totalLives = members.reduce(
    (sum, m) => (isEnrolling(m) ? sum + 1 + m.dependents.length : sum),
    0,
  );

  // A dependent is optional, but once added it's mandatory — every field must be
  // filled in (or the row removed) before the census can be submitted. Without
  // this the form happily submitted blank dependents.
  const incompleteMembers = members.filter((m) => !memberDependentsComplete(m));
  const canSubmit = incompleteMembers.length === 0;

  const submit = () => {
    if (!canSubmit) return;
    // Mark anyone we didn't touch as resolved with manual collection on submit.
    onChange(members.map((m) => ({ ...m, dependentMethod: "manual" })));
    onSubmit();
  };

  const { eyebrowMotion, titleMotion, descriptionMotion } = sectionPageIntroMotions(
    playBuild,
    ceremony,
  );

  return (
    <V2EnrollmentFlowMain width="default">
      <div className="mx-auto w-full max-w-cw-v2-content-narrow pb-8">
        <div className="mb-8 flex flex-col items-center gap-2 text-center">
          <V2Typography
            variant="overline"
            {...mergeStepBuildProps(eyebrowMotion, { className: "text-cw-v2-reliant-red" })}
          >
            One step from your quote
          </V2Typography>
          <V2Typography variant="display" as="h1" {...mergeStepBuildProps(titleMotion)}>
            Confirm who&rsquo;s actually covered
          </V2Typography>
          <V2Typography
            variant="body-large"
            muted
            {...mergeStepBuildProps(descriptionMotion, { className: "max-w-xl" })}
          >
            A bindable medical rate needs each person&rsquo;s dependents. Add their spouse and
            children below, or waive anyone covered elsewhere.
          </V2Typography>
        </div>

        <V2Card className="overflow-hidden">
          <V2CardContent className="flex flex-col gap-5 p-6 sm:p-8">
            <div className="flex flex-col gap-1">
              <V2Typography variant="heading-small" as="h2" className="font-semibold">
                Add dependents for {team} {team === 1 ? "person" : "people"}
              </V2Typography>
              <V2Typography variant="body" muted>
                For each person, add their spouse and children with birthdates. Leave a person empty
                if they have no dependents.
              </V2Typography>
            </div>

            <div className="flex flex-col divide-y divide-cw-v2-border">
              {members.map((member, index) => {
                const name =
                  `${member.firstName} ${member.lastName}`.trim() || `Person ${index + 1}`;
                const spouse = hasSpouse(member);
                const kids = kidsOf(member);
                const enrolling = isEnrolling(member);
                return (
                  <div key={member.id} className="flex flex-col gap-3 py-4">
                    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
                      <div className={cn("flex items-center gap-3", !enrolling && "opacity-50")}>
                        <Avatar member={member} index={index} />
                        {/*
                          `data-sensitive`: a member's full name rendered as TEXT, which
                          `maskAllInputs` does not reach — it masks input values only. The
                          dependent inputs below were already tagged; this row was not, so a
                          replay of this step recorded every employee's name in plain sight.
                        */}
                        <V2Typography
                          variant="body-small"
                          className="font-medium text-cw-v2-ink"
                          data-sensitive
                        >
                          {name}
                        </V2Typography>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        {enrolling ? (
                          <>
                            <button
                              type="button"
                              aria-pressed={spouse}
                              onClick={() => toggleSpouse(member)}
                              data-track="button-company-census-dependent-spouse"
                              className={cn(
                                "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 transition-colors",
                                spouse
                                  ? "border-cw-v2-accent bg-cw-v2-accent/10 text-cw-v2-ink"
                                  : "border-cw-v2-border text-cw-v2-text-muted hover:text-cw-v2-ink",
                              )}
                            >
                              {spouse ? <Check className="h-3 w-3 text-cw-v2-accent" /> : null}
                              <V2Typography variant="meta" as="span" className="font-semibold">
                                Spouse
                              </V2Typography>
                            </button>

                            <button
                              type="button"
                              onClick={() => addKid(member)}
                              disabled={kids.length >= MAX_KIDS}
                              data-track="button-company-census-dependent-add-child"
                              className="inline-flex items-center gap-1 rounded-full border border-cw-v2-border px-2.5 py-1 text-cw-v2-text-muted transition-colors hover:text-cw-v2-ink disabled:opacity-40"
                            >
                              <Plus className="h-3 w-3" />
                              <V2Typography variant="meta" as="span" className="font-semibold">
                                Add a child
                              </V2Typography>
                            </button>

                            <span aria-hidden className="mx-0.5 h-4 w-px bg-cw-v2-border" />
                          </>
                        ) : null}

                        <button
                          type="button"
                          aria-pressed={!enrolling}
                          onClick={() => toggleEnrolling(member)}
                          data-track="button-company-census-dependent-enrolling"
                          className={cn(
                            "inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 transition-colors",
                            enrolling
                              ? "border-cw-v2-border text-cw-v2-text-muted hover:text-cw-v2-ink"
                              : "border-cw-v2-border border-dashed bg-cw-v2-sidewalk text-cw-v2-ink",
                          )}
                        >
                          <UserMinus className="h-3 w-3" />
                          <V2Typography variant="meta" as="span" className="font-semibold">
                            {enrolling ? "Not enrolling" : "Waived — re-add"}
                          </V2Typography>
                        </button>
                      </div>
                    </div>

                    {!enrolling ? (
                      <div className="sm:pl-9">
                        <V2Select
                          value={member.notEnrollingReason ?? undefined}
                          onValueChange={(next) => setWaiveReason(member, next as WaiveReason)}
                        >
                          <V2SelectTrigger
                            id={`${member.id}-waive-reason`}
                            label="Why aren't they enrolling?"
                            size="sm"
                            aria-label="Why aren't they enrolling?"
                            className="max-w-sm"
                            data-track="select-company-census-waive-reason"
                          >
                            <V2SelectValue placeholder="Select a reason" />
                          </V2SelectTrigger>
                          <V2SelectContent>
                            {WAIVE_REASONS.map((option) => (
                              <V2SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </V2SelectItem>
                            ))}
                          </V2SelectContent>
                        </V2Select>
                      </div>
                    ) : null}

                    {enrolling && member.dependents.length > 0 ? (
                      <div className="sm:pl-9">
                        <div className="flex flex-col gap-3 rounded-[12px] border border-cw-v2-border p-4">
                          <SameZipToggle
                            pressed={sameZip(member)}
                            memberFirstName={member.firstName.trim()}
                            onToggle={() => toggleSameZip(member)}
                          />
                          {member.dependents.map((dep) => {
                            const childIndex = kids.findIndex((k) => k.id === dep.id);
                            const label =
                              dep.relationship === "spouse" ? "Spouse" : `Child ${childIndex + 1}`;
                            // A blank required field only turns red once the
                            // employer has left it (blurred) — never while they're
                            // still typing. Format errors (bad DOB/ZIP) show as
                            // soon as there's a value to be wrong.
                            const firstNameError =
                              isTouched(dep.id, "firstName") && !dep.firstName.trim()
                                ? "Required"
                                : undefined;
                            const lastNameError =
                              isTouched(dep.id, "lastName") && !dep.lastName.trim()
                                ? "Required"
                                : undefined;
                            const sexError =
                              isTouched(dep.id, "sex") && !dep.sex ? "Required" : undefined;
                            const dobError =
                              dep.dateOfBirth.length > 0
                                ? validDateOfBirth(dep.dateOfBirth)
                                  ? undefined
                                  : "Enter a valid date of birth."
                                : isTouched(dep.id, "dob")
                                  ? "Required"
                                  : undefined;
                            const zipError = sameZip(member)
                              ? undefined
                              : dep.zip.length > 0
                                ? validZip(dep.zip)
                                  ? undefined
                                  : "ZIP must be 5 or 9 digits."
                                : isTouched(dep.id, "zip")
                                  ? "Required"
                                  : undefined;
                            return (
                              <div
                                key={dep.id}
                                className="flex flex-col gap-3 rounded-[12px] border border-cw-v2-border bg-cw-v2-surface p-3"
                              >
                                <div className="flex items-center justify-between">
                                  <V2Typography
                                    variant="meta"
                                    as="span"
                                    className="font-semibold text-cw-v2-ink"
                                  >
                                    {label}
                                  </V2Typography>
                                  <V2Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="shrink-0"
                                    onClick={() => removeDependent(member, dep.id)}
                                    aria-label={`Remove ${label} for ${name}`}
                                    data-track="button-company-census-dependent-remove"
                                  >
                                    <X className="h-4 w-4" />
                                  </V2Button>
                                </div>
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                  <V2Input
                                    label="First name"
                                    size="sm"
                                    value={dep.firstName}
                                    onChange={(e) =>
                                      patchDependent(member, dep.id, { firstName: e.target.value })
                                    }
                                    onBlur={markTouched(dep.id, "firstName")}
                                    error={firstNameError}
                                    autoComplete="off"
                                    data-sensitive
                                  />
                                  <V2Input
                                    label="Last name"
                                    size="sm"
                                    value={dep.lastName}
                                    onChange={(e) =>
                                      patchDependent(member, dep.id, { lastName: e.target.value })
                                    }
                                    onBlur={markTouched(dep.id, "lastName")}
                                    error={lastNameError}
                                    autoComplete="off"
                                    data-sensitive
                                  />
                                  <DependentSexSelect
                                    id={dep.id}
                                    value={dep.sex}
                                    error={sexError}
                                    onChange={(sex) => {
                                      markTouched(dep.id, "sex")();
                                      patchDependent(member, dep.id, { sex });
                                    }}
                                  />
                                  <V2DateInput
                                    label={`${label} birthdate`}
                                    size="sm"
                                    value={dep.dateOfBirth}
                                    min="1900-01-01"
                                    max={todayInputValue()}
                                    onValueChange={(value) =>
                                      patchDependent(member, dep.id, { dateOfBirth: value })
                                    }
                                    onBlur={markTouched(dep.id, "dob")}
                                    error={dobError}
                                    data-sensitive
                                  />
                                  {!sameZip(member) ? (
                                    <V2Input
                                      label="Home ZIP code"
                                      size="sm"
                                      value={dep.zip}
                                      onChange={(e) =>
                                        patchDependent(member, dep.id, {
                                          zip: formatZip(e.target.value),
                                        })
                                      }
                                      onBlur={markTouched(dep.id, "zip")}
                                      inputMode="numeric"
                                      maxLength={10}
                                      placeholder="94103"
                                      name="postal-code"
                                      autoComplete="off"
                                      error={zipError}
                                      prefix={<MapPin className="h-4 w-4" aria-hidden />}
                                      data-sensitive
                                    />
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-col">
                <V2Typography variant="overline" muted>
                  Covered lives{" "}
                  <span className="font-bold text-base text-cw-v2-ink tabular-nums">
                    {totalLives}
                  </span>
                </V2Typography>
                <V2Typography variant="meta" muted>
                  {enrolledCount} of {team} enrolling
                </V2Typography>
              </div>
              <V2Button
                type="button"
                variant="primary"
                size="lg"
                disabled={isSubmitting || !canSubmit}
                onClick={submit}
                iconSuffix={<ArrowRight className="h-4 w-4" />}
                data-track="button-company-census-dependents-submit"
              >
                {isSubmitting ? "Submitting…" : "Submit"}
              </V2Button>
            </div>

            {!canSubmit ? (
              <V2Typography variant="meta" muted>
                {members.some((m) => !isEnrolling(m) && !m.notEnrollingReason)
                  ? "Tell us why each waived person isn't enrolling, and finish every dependent you've added — or remove them to continue."
                  : "Finish every dependent you've added — first name, last name, gender, and birthdate — or remove them to continue."}
              </V2Typography>
            ) : null}

            {submitError ? (
              <V2Typography variant="meta" className="text-cw-v2-reliant-red">
                {submitError}
              </V2Typography>
            ) : null}
          </V2CardContent>
        </V2Card>

        <V2FormActions className="mt-8">
          <V2Button
            variant="link"
            onClick={onBack}
            disabled={isSubmitting}
            data-track="button-company-census-dependents-back"
          >
            Back to your team
          </V2Button>
        </V2FormActions>
      </div>
    </V2EnrollmentFlowMain>
  );
}

/**
 * Per-employee toggle: when on (default) every dependent inherits the
 * employee's home ZIP and the per-dependent ZIP field is hidden. Turning it off
 * reveals a ZIP field on each dependent, pre-filled from the employee.
 */
function SameZipToggle({
  pressed,
  memberFirstName,
  onToggle,
}: {
  pressed: boolean;
  memberFirstName: string;
  onToggle: () => void;
}) {
  const who = memberFirstName ? `${memberFirstName}'s` : "the employee's";
  const label = `Dependents live at ${who} ZIP`;
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex flex-col">
        <V2Typography variant="meta" as="span" className="font-semibold text-cw-v2-ink">
          {label}
        </V2Typography>
        <V2Typography variant="meta" muted as="span">
          Turn off to enter a different ZIP for each dependent.
        </V2Typography>
      </span>
      <V2Switch
        checked={pressed}
        onCheckedChange={onToggle}
        aria-label={label}
        data-track="button-company-census-dependent-same-zip"
      />
    </div>
  );
}

function DependentSexSelect({
  id,
  value,
  error,
  onChange,
}: {
  id: string;
  value: Sex | "";
  error?: string;
  onChange: (sex: Sex) => void;
}) {
  return (
    <div className="cw-v2-input-field">
      <V2Select value={value || undefined} onValueChange={(next) => onChange(next as Sex)}>
        <V2SelectTrigger
          id={`${id}-sex`}
          label="Gender"
          size="sm"
          aria-label="Gender"
          error={error}
        >
          <V2SelectValue placeholder="Select" />
        </V2SelectTrigger>
        <V2SelectContent>
          {SEX_OPTIONS.map((option) => (
            <V2SelectItem key={option.value} value={option.value}>
              {option.label}
            </V2SelectItem>
          ))}
        </V2SelectContent>
      </V2Select>
    </div>
  );
}
